import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  Card,
  Table,
  Grid,
  Tabs,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconFileText,
  IconDownload,
  IconTrash,
  IconUser,
  IconBuilding,
  IconCalendar,
  IconCurrencyDollar,
  IconPackage,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useBidSubmissionDetails } from "../hooks/useBidSubmissions";
import { useTenderById } from "../hooks/useTenders";
import { useRemoveBidSubmissionDocument, useDownloadBidSubmissionDocument } from "../hooks/useBidSubmissionDocuments";
import { bidSubmissionDocumentsApi } from "../../../api/bidSubmissionDocuments";
import { notifications } from "@mantine/notifications";
import { formatCurrency } from '../../../utils/currency';
import dayjs from "dayjs";

export function BidSubmissionDetail() {
  const navigate = useNavigate();
  const { tenderId, submissionId } = useParams<{ tenderId: string; submissionId: string }>();
  const { data: bidSubmission, isLoading, error, refetch } = useBidSubmissionDetails(tenderId!, submissionId!);
  const { data: tender } = useTenderById(tenderId!);
  const removeDocument = useRemoveBidSubmissionDocument();
  const downloadDocument = useDownloadBidSubmissionDocument();

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading bid submission details...</Text>
      </Container>
    );
  }

  if (error || !bidSubmission) {
    return (
      <Container size="xl" py="xl">
        <Text color="red">Failed to load bid submission details</Text>
      </Container>
    );
  }

  const handleRemoveDocument = async (documentId: string) => {
    try {
      await removeDocument.mutateAsync(documentId);
      notifications.show({
        title: "Success",
        message: "Document removed successfully",
        color: "green",
      });
      refetch();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to remove document",
        color: "red",
      });
    }
  };

  const handleDownloadDocument = async (documentId: string, fileName: string) => {
    try {
      // Admin users don't need vendor auth
      const response = await bidSubmissionDocumentsApi.downloadDocument(documentId, false);
      
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      notifications.show({
        title: "Success",
        message: "Document downloaded successfully",
        color: "green",
      });
    } catch (error: any) {
      console.error("Error downloading document:", error);
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to download document",
        color: "red",
      });
    }
  };

  const getDocumentTypeDisplayName = (type: string) => {
    const typeMap: Record<string, string> = {
      QUOTATION: "Quotation",
      COMPANY_PROFILE: "Company Profile",
      COMPANY_REGISTRATION: "Company Registration",
      GST_REGISTRATION: "GST Registration",
      EXPERIENCE_LETTER: "Experience Letter",
      TECHNICAL_PROPOSAL: "Technical Proposal",
      FINANCIAL_PROPOSAL: "Financial Proposal",
      TAX_CLEARANCE: "Tax Clearance",
      INSURANCE: "Insurance",
      FINANCIAL_STATEMENT: "Financial Statement",
      CUSTOM: "Custom Document",
    };
    return typeMap[type] || type.replace(/_/g, " ");
  };

  return (
    <Container size="xl" py="xl">
      <Paper shadow="sm" p="xl">
        <Group mb="xl">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(`/tenders/${tenderId}`)}
          >
            Back to Tender
          </Button>
        </Group>

        <Group justify="space-between" mb="xl">
          <div>
            <Title order={2}>Bid Submission Details</Title>
            <Text c="dimmed" size="sm">
              {tender?.description} • Submitted on{" "}
              {dayjs(bidSubmission.submittedAt).format("MMM DD, YYYY HH:mm")}
            </Text>
          </div>
        </Group>

        <Tabs defaultValue="overview">
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconUser size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="documents" leftSection={<IconFileText size={16} />}>
              Documents
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="xl">
            <Grid>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Stack gap="xl">
                  {/* Company Information */}
                  <Card shadow="sm" p="lg">
                    <Title order={3} mb="md" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IconBuilding size={20} />
                      Company Information
                    </Title>
                    <Stack gap="md">
                      <Group grow>
                        <div>
                          <Text size="sm" c="dimmed">Company Name</Text>
                          <Text fw={500}>{bidSubmission.companyName}</Text>
                        </div>
                        <div>
                          <Text size="sm" c="dimmed">Representative</Text>
                          <Text>{bidSubmission.representativeName || "Not specified"}</Text>
                        </div>
                      </Group>
                      {bidSubmission.representativeEmail && (
                        <Group grow>
                          <div>
                            <Text size="sm" c="dimmed">Email</Text>
                            <Text>{bidSubmission.representativeEmail}</Text>
                          </div>
                          {bidSubmission.representativePhone && (
                            <div>
                              <Text size="sm" c="dimmed">Phone</Text>
                              <Text>{bidSubmission.representativePhone}</Text>
                            </div>
                          )}
                        </Group>
                      )}
                    </Stack>
                  </Card>

                  {/* Bid Information */}
                  <Card shadow="sm" p="lg">
                    <Title order={3} mb="md" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IconCurrencyDollar size={20} />
                      Bid Information
                    </Title>
                    <Stack gap="md">
                      <Group grow>
                        <div>
                          <Text size="sm" c="dimmed">Total Price</Text>
                          <Text fw={500} size="lg">
                            {formatCurrency(Number(bidSubmission.totalPrice), tender?.currency || 'MVR')}
                          </Text>
                        </div>
                        <div>
                          <Text size="sm" c="dimmed">Qualification Status</Text>
                          <Badge 
                            color={bidSubmission.qualificationStatus === 'QUALIFIED' ? 'green' : 
                                   bidSubmission.qualificationStatus === 'DISQUALIFIED' ? 'red' : 'yellow'}
                          >
                            {bidSubmission.qualificationStatus}
                          </Badge>
                        </div>
                      </Group>
                      {bidSubmission.qualificationNotes && (
                        <div>
                          <Text size="sm" c="dimmed">Qualification Notes</Text>
                          <Text>{bidSubmission.qualificationNotes}</Text>
                        </div>
                      )}
                    </Stack>
                  </Card>

                  {/* Bid Items */}
                  {bidSubmission.bidItems && bidSubmission.bidItems.length > 0 && (
                    <Card shadow="sm" p="lg">
                      <Title order={3} mb="md" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <IconPackage size={20} />
                        Bid Items ({bidSubmission.bidItems.length})
                      </Title>
                      <Stack gap="md">
                        {bidSubmission.bidItems.map((item) => (
                          <Card key={item.id} shadow="xs" p="md" withBorder>
                            <Group justify="space-between" mb="sm">
                              <div>
                                <Text fw={500}>{item.tenderItem?.itemName || 'Unknown Item'}</Text>
                                {item.tenderItem?.description && (
                                  <Text size="sm" c="dimmed">{item.tenderItem.description}</Text>
                                )}
                              </div>
                              <Badge size="sm" variant="light">
                                #{item.order}
                              </Badge>
                            </Group>
                            <Group gap="lg">
                              <div>
                                <Text size="xs" c="dimmed">Quantity</Text>
                                <Text size="sm">{item.quantity} {item.unit}</Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed">Unit Price</Text>
                                <Text size="sm" fw={500}>
                                  {formatCurrency(Number(item.price), tender?.currency || 'MVR')}
                                </Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed">Subtotal</Text>
                                <Text size="sm" fw={500} color="blue">
                                  {formatCurrency(Number(item.price) * item.quantity, tender?.currency || 'MVR')}
                                </Text>
                              </div>
                            </Group>
                          </Card>
                        ))}
                      </Stack>
                    </Card>
                  )}
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack gap="md">
                  {/* Submission Details */}
                  <Card shadow="sm" p="lg">
                    <Title order={3} mb="md" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IconCalendar size={20} />
                      Submission Details
                    </Title>
                    <Stack gap="md">
                      <div>
                        <Text size="sm" c="dimmed">Submitted At</Text>
                        <Text>{dayjs(bidSubmission.submittedAt).format("MMM DD, YYYY HH:mm")}</Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">Documents Uploaded</Text>
                        <Text>{bidSubmission.documents?.length || 0} documents</Text>
                      </div>
                    </Stack>
                  </Card>
                </Stack>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          <Tabs.Panel value="documents" pt="xl">
            <Card shadow="sm" p="lg">
              <Group justify="space-between" mb="md">
                <Title order={3}>
                  Documents ({bidSubmission.documents?.length || 0})
                </Title>
              </Group>
              
              {bidSubmission.documents && bidSubmission.documents.length > 0 ? (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Document Name</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Uploaded At</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {bidSubmission.documents.map((doc) => (
                      <Table.Tr key={doc.id}>
                        <Table.Td>
                          <Group gap="xs" align="flex-start">
                            <IconFileText size={16} color="var(--mantine-color-blue-6)" />
                            <div>
                              <Text fw={500}>{doc.title || doc.fileName || doc.originalName}</Text>
                              {doc.description && (
                                <Text size="xs" c="dimmed">
                                  {doc.description}
                                </Text>
                              )}
                            </div>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="sm" variant="light">
                            {getDocumentTypeDisplayName(doc.documentType)}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {dayjs(doc.uploadedAt).format("MMM DD, YYYY HH:mm")}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Download Document">
                              <ActionIcon
                                variant="light"
                                color="blue"
                                size="sm"
                                onClick={() => handleDownloadDocument(doc.id, doc.fileName || doc.originalName || 'document')}
                                loading={downloadDocument.isPending}
                              >
                                <IconDownload size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Remove Document">
                              <ActionIcon
                                variant="light"
                                color="red"
                                size="sm"
                                onClick={() => handleRemoveDocument(doc.id)}
                                loading={removeDocument.isPending}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text c="dimmed">No documents uploaded yet</Text>
              )}
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Paper>

    </Container>
  );
}
